import type { DataSource, FieldSchema, ReportData } from "../types/data.js";
import type { PathSegment } from "../expr/ast.js";
import type { SchemaScope } from "../expr/typecheck.js";
import { resolvePathSchema } from "../expr/typecheck.js";

/**
 * Synthesises plausible sample data from a schema. Used in design mode when the
 * host provides no sample data so tables, charts and KPIs still show something.
 */
export function synthesizeSampleData(sources: DataSource[], opts: { arrayLength?: number; now?: Date } = {}): ReportData {
  const out: ReportData = {};
  for (const s of sources) out[s.id] = synthesizeObject(s.schema, opts, 0);
  return out;
}

export function synthesizeValue(schema: FieldSchema, opts: { arrayLength?: number; now?: Date } = {}, depth = 0, index = 0): unknown {
  if (schema.example !== undefined) return schema.example;
  if (schema.enum?.length) return schema.enum[index % schema.enum.length];
  const now = opts.now ?? new Date();
  switch (schema.type) {
    case "string":
      return schema.label ?? "Sample text";
    case "number":
      return [1240, 875, 2310, 1580, 990, 1870][index % 6];
    case "currency":
      return [125000, 48200.5, 231000, 15800, 99000, 187500][index % 6];
    case "percent":
      return [0.125, 0.082, 0.31, 0.058, 0.196, 0.229][index % 6];
    case "date":
    case "datetime": {
      const d = new Date(now);
      d.setMonth(d.getMonth() - index);
      return d.toISOString();
    }
    case "boolean":
      return index % 2 === 0;
    case "image":
      return undefined;
    case "url":
      return "https://example.com";
    case "richtext":
      return "Sample rich text";
    case "object":
      return depth > 6 ? {} : synthesizeObject(schema.fields ?? {}, opts, depth + 1);
    case "array": {
      const n = opts.arrayLength ?? 4;
      const item = schema.itemSchema ?? { type: "string" as const };
      return depth > 6 ? [] : Array.from({ length: n }, (_, i) => synthesizeValue(item, opts, depth + 1, i));
    }
    default:
      return undefined;
  }
}

function synthesizeObject(fields: Record<string, FieldSchema>, opts: { arrayLength?: number; now?: Date }, depth: number): Record<string, unknown> {
  const o: Record<string, unknown> = {};
  let i = 0;
  for (const [k, f] of Object.entries(fields)) o[k] = synthesizeValue(f, opts, depth, i++);
  return o;
}

/** Human label for a data path, e.g. "Client › First name". */
export function labelForPath(segments: PathSegment[], scope: SchemaScope): string {
  const parts: string[] = [];
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]!;
    if (seg.kind === "index") {
      parts[parts.length - 1] = `${parts[parts.length - 1] ?? ""} #${seg.index + 1}`;
      continue;
    }
    const r = resolvePathSchema(segments.slice(0, i + 1), scope);
    parts.push(r.schema?.label ?? humanizeKey(seg.name));
  }
  return parts.join(" › ");
}

export function humanizeKey(key: string): string {
  return key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());
}
